import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ArcGISParcelMap } from "../components/ArcGISParcelMap";
import { getParcelById, getSubParcelsByParcelId, Parcel, SubParcel } from "../api/dataverse";
import { dataverseScopes } from "../auth/msalConfig";

export const ParcelDetailPage: React.FC = () => {
  const { parcelId } = useParams<{ parcelId: string }>();
  const [parcel, setParcel] = useState<Parcel | null>(null);
  const [subParcels, setSubParcels] = useState<SubParcel[]>([]);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<{ name: string; webUrl: string; size: number; createdDate: string }[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const { instance } = useMsal();

  useEffect(() => {
    if (!parcelId) return;
    setLoading(true);
    let token: string;
    instance.acquireTokenSilent({
      scopes: dataverseScopes,
      account: instance.getActiveAccount() || undefined,
    })
      .then((tokenResponse) => {
        token = tokenResponse.accessToken;
        return getParcelById(parcelId, token);
      })
      .then((p) => {
        setParcel(p);
        if (p) return getSubParcelsByParcelId(p.id, token);
        return [];
      })
      .then(setSubParcels)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [parcelId, instance]);

  // Fetch SharePoint documents once the parcel record is loaded
  useEffect(() => {
    if (!parcel?.id || !parcel?.parcelId) return;
    setDocsLoading(true);
    const apiBase = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:7071/api" : "/api");
    fetch(`${apiBase}/parcel/${parcel.id}/documents?parcelId=${encodeURIComponent(parcel.parcelId)}`)
      .then(r => r.json())
      .then(data => setDocuments(data.value ?? []))
      .catch(console.error)
      .finally(() => setDocsLoading(false));
  }, [parcel?.id, parcel?.parcelId]);

  const statusColor: Record<string, string> = {
    "Disponible": "bg-green-100 text-green-700 border-green-200",
    "Arrendada": "bg-blue-100 text-blue-700 border-blue-200",
    "Vendida": "bg-red-100 text-red-700 border-red-200",
    "En Proceso": "bg-amber-100 text-amber-700 border-amber-200",
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64 text-gray-400">Cargando parcela...</div>
      </div>
    );
  }

  if (!parcel) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Parcela no encontrada</h2>
          <p className="text-gray-500 mb-6">La parcela "{parcelId}" no existe o no está disponible.</p>
          <Link to="/parcelas" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
            Volver a Parcelas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/parcelas" className="hover:text-blue-600 transition">Parcelas</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{parcel.parcelId}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Parcela {parcel.parcelId}</h1>
          <p className="text-gray-500 mt-1">{parcel.municipio}</p>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${statusColor[parcel.availabilityStatus] ?? "bg-gray-100 text-gray-700 border-gray-200"}`}>
          {parcel.availabilityStatus}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Property Info */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Información de la Parcela</h3>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="px-5 py-3 flex justify-between">
                <span className="text-sm text-gray-500">Parcel ID</span>
                <span className="text-sm font-medium text-gray-900">{parcel.parcelId}</span>
              </div>
              <div className="px-5 py-3 flex justify-between">
                <span className="text-sm text-gray-500">Finca ID</span>
                <span className="text-sm font-medium text-gray-900">{parcel.fincaId || "—"}</span>
              </div>
              <div className="px-5 py-3 flex justify-between">
                <span className="text-sm text-gray-500">Municipio</span>
                <span className="text-sm font-medium text-gray-900">{parcel.municipio}</span>
              </div>
              <div className="px-5 py-3 flex justify-between">
                <span className="text-sm text-gray-500">Cabida</span>
                <span className="text-sm font-medium text-gray-900">{parcel.cabida > 0 ? `${parcel.cabida} cuerdas` : "—"}</span>
              </div>
              <div className="px-5 py-3 flex justify-between">
                <span className="text-sm text-gray-500">Estado</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor[parcel.availabilityStatus] ?? "bg-gray-100 text-gray-700"}`}>
                  {parcel.availabilityStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Acquisition Info */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Detalles Adicionales</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {parcel.nombreAdquisicion && (
                <div className="px-5 py-3">
                  <span className="text-xs text-gray-500 block mb-1">Nombre de Adquisición</span>
                  <span className="text-sm text-gray-900">{parcel.nombreAdquisicion}</span>
                </div>
              )}
              {parcel.uso && (
                <div className="px-5 py-3">
                  <span className="text-xs text-gray-500 block mb-1">Uso Programado</span>
                  <span className="text-sm text-gray-900">{parcel.uso}</span>
                </div>
              )}
              {parcel.description && (
                <div className="px-5 py-3">
                  <span className="text-xs text-gray-500 block mb-1">Descripción</span>
                  <span className="text-sm text-gray-900">{parcel.description}</span>
                </div>
              )}
              {(parcel.xCentroid !== 0 && parcel.yCentroid !== 0) && (
                <div className="px-5 py-3">
                  <span className="text-xs text-gray-500 block mb-1">Coordenadas</span>
                  <span className="text-sm text-gray-900 font-mono">
                    {parcel.yCentroid.toFixed(6)}, {parcel.xCentroid.toFixed(6)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Documentos</h3>
            </div>
            {docsLoading ? (
              <div className="px-5 py-4 text-sm text-gray-400">Cargando documentos...</div>
            ) : documents.length === 0 ? (
              <div className="px-5 py-4 text-sm text-gray-400">No hay documentos disponibles.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {documents.map((doc) => (
                  <a
                    key={doc.webUrl}
                    href={doc.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-blue-50 transition group"
                  >
                    <svg className="w-7 h-7 text-red-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM12 17H8v-1h4v1zm4-3H8v-1h8v1zm0-3H8v-1h8v1z"/>
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 group-hover:text-blue-700 truncate">{doc.name}</p>
                      <p className="text-xs text-gray-400">
                        {(doc.size / 1024).toFixed(0)} KB · {new Date(doc.createdDate).toLocaleDateString("es-PR")}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

   {/* Map + Sub-parcels */}
        <div className="lg:col-span-2 space-y-6">
          <ArcGISParcelMap fincaId={parcel.parcelId} height="500px" />

          {/* Sub-parcels */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Sub-parcelas</h3>
              {subParcels.length > 0 && (
                <span className="text-xs text-gray-500">{subParcels.length} sub-parcela{subParcels.length !== 1 ? "s" : ""}</span>
              )}
            </div>
            {subParcels.length === 0 ? (
              <div className="px-5 py-6 text-sm text-gray-400 text-center">No hay sub-parcelas registradas</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Municipio</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cabida</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Uso</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Estatus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subParcels.map((sp) => (
                    <tr key={sp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{sp.subParcelId || "—"}</td>
                      <td className="px-4 py-2 text-gray-600">{sp.municipio}</td>
                      <td className="px-4 py-2 text-gray-600">{sp.cabida > 0 ? `${sp.cabida} cuerdas` : "—"}</td>
                      <td className="px-4 py-2 text-gray-600">{sp.uso || "—"}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{sp.estatus}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
